/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Async, TMap } from '../../helpers/types';
import { LiteProgram, ProgramTabHash } from './domain';
import auth from '../../../components/auth';
import { faBolt, faArchive } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useState } from 'react';
import { useCollectionModel } from './collection.model';
import {
  BusinessModel,
  Influencer,
  Program,
} from '@playbook-technologies/common-enums';
import { Collection, CollectionProgram } from './collection.domain';
import {
  formatPrice,
  getMappedCollection,
  getPublishInfo,
  getSuccessModalData,
  getType,
  toLiteProgram,
  toOtpTabs,
  toSubscriptionTabs,
} from './mappers';
import { useChallengesCount } from '../challenges/model/Challenges.model';
import { browserHistory } from 'react-router';
import { PATHS } from '@client/routes';
import { ProgramStateKind } from '../programs/model/programs.types';

export type ProgramPageProps = {
  tabs: Partial<TMap<ProgramTabHash, { isReady: boolean }>>;
  publishAction: {
    icon?: IconProp;
    title: string;
    onClick: () => void;
    disabled: boolean;
    loading: boolean;
  } | null;
  publishModal: {
    isOpen: boolean;
    onClose: () => void;
    publish: () => void;
    showExamplesModal: boolean;
    closeExamplesModal: () => void;
    openExamplesModal: () => void;
    alert?: string;
    firstTimePublishInfo?: string;
    hideSaveAsDraft: boolean;
    isCreatorLive: boolean;
  };
  publishToBundleModal: {
    isOpen: boolean;
    close: () => void;
    publish: () => void;
    setPublishToPlatformBundle: (value: boolean) => void;
    isPlatformBundleSelected: boolean;
    programId: number;
  };
  removeFromBundleModal: {
    isOpen: boolean;
    close: () => void;
    archive: () => void;
    programId: number;
  };
  archiveModal: {
    isOpen: boolean;
    close: () => void;
    archive: () => void;
  };
  successModal: {
    isOpen: boolean;
    onClose: () => void;
    subtitle?: string;
    title: string;
    details?: string;
    bottomComponent?: React.ReactNode;
    links?: Array<{
      url: string;
      title: string;
      urlClassName?: string;
      wrapperClassName?: string;
    }>;
    bottomText?: string;
  };
  programCard: {
    title: string;
    subtitle: string;
    thumbnailUrl: string;
    price: string;
    discountedPrice: string | null;
  };
  tabProps: {
    disabledCollectionType: boolean;
    collection: Collection;
    originalAccessType: Collection['accessType'];
    validationTree: any;
    liteProgram: LiteProgram;
    onChange: (p: Collection) => void;
    typeChanged: (t: number) => void;
    params: {
      influencerId: string;
      programId: string;
    };
    save: (delta?: Partial<CollectionProgram>) => Promise<boolean>;
    refetchCollection: () => void;
    router: any;
    route: any;
  };
  validationErrors: {
    errors: Array<string>;
    onClear: () => void;
  };
  readonly: boolean;
};

const VALID_PROGRAM_STATES = [
  'draft',
  'ready',
  'published',
  'archived',
] as const satisfies readonly ProgramStateKind[];

const parseProgramStateKind = (raw: string | null): ProgramStateKind | null => {
  if (!raw) return null;
  const found = VALID_PROGRAM_STATES.find((s) => s === raw);
  return found ?? null;
};

export const useCollectionVM = ({
  collectionId,
  creatorId,
  router,
  route,
}: {
  collectionId: number;
  creatorId: number;
  router: any;
  route: any;
}): Async<ProgramPageProps> => {
  const [publishState, setPublishState] = useState({
    showExamplesModal: false,
    showPublishModal: false,
    showArchiveModal: false,
    showPublishToBundleModal: false,
    showRemoveFromBundleModal: false,
  });
  const challengesCount = useChallengesCount(collectionId);

  const derivedState = parseProgramStateKind(
    router?.location?.query?.programDerivedState ?? null,
  );
  const asyncModel = useCollectionModel({
    collectionId,
    creatorId,
    derivedState,
  });

  if (
    asyncModel.type === 'fetching' ||
    asyncModel.type === 'fetch-error' ||
    asyncModel.type === 'not-fetched'
  )
    return asyncModel;
  if (challengesCount.type !== 'fetched') return challengesCount;

  const model = asyncModel.value;

  const customURL = model.creator?.customURL || '';
  const profileDeepLink = model.creator?.profileDeepLink || '';
  const hasPublishedAnything =
    model.creator.mode === Influencer.Mode.Live.value ||
    !!model.creator.publisheCollectionsCount;
  const isBundleModel = model.creator.businessModel === BusinessModel.Bundle;

  const liteProgram = toLiteProgram({
    customURL,
    salesPageConfig: model.salesPageConfig,
    collection: model.collection as any,
    price: model.price,
  });

  const isPublished =
    !!model.collection.publishedAt && !model.collection.archivedAt;
  const isArchived = !!model.collection.archivedAt;
  const programType = getType(model.collection.accessType);
  const tabs = (programType === 'otp' ? toOtpTabs : toSubscriptionTabs)(
    liteProgram,
  );

  const startPublish = () => {
    if (model.collection.accessType === Program.AccessType.Exclusive.value) {
      setPublishState((prev) => ({ ...prev, showPublishModal: true }));
      return;
    }
    const isBundleCreator =
      model.creator.businessModel === BusinessModel.Bundle;
    if (isBundleCreator && model.hasPlatformBundle) {
      setPublishState((prev) => ({
        ...prev,
        showPublishModal: false,
        showPublishToBundleModal: isBundleCreator,
      }));
      return;
    }
    model.publishProgram();
  };

  const getPublishAction = (publishing: boolean) => {
    if (isPublished) {
      return {
        onClick: () =>
          setPublishState((prev) => ({
            ...prev,
            showArchiveModal: !isBundleModel,
            showRemoveFromBundleModal: isBundleModel,
          })),
        title: 'Archive',
        icon: faArchive,
        disabled: publishing,
        loading: publishing,
      };
    }

    const hasExclusivePricing =
      !!model.collection.exclusiveProgramMetadata?.price ||
      !!model.collection.exclusiveProgramMetadata?.originalPrice;

    if (programType === 'otp' && !hasExclusivePricing) {
      return {
        onClick: async () => {
          await model.save();
        },
        title: 'Save',
        disabled: false,
        loading: false,
      };
    }

    return {
      onClick: startPublish,
      title: 'Publish',
      icon: faBolt,
      disabled: publishing,
      loading: publishing,
    };
  };

  const alert =
    model.collection.accessType == Program.AccessType.Exclusive.value
      ? "Once your program is published, you won't be able to delete or edit workouts. Ensure your offering is solid and set up for success."
      : undefined;

  const isCreatorView = auth.isInfluencer();
  const isProgramArchived = model.collection.archivedAt !== null;
  const isOtp = programType === 'otp';
  const readonly = isProgramArchived && isCreatorView && isOtp;
  const isCreatorLive = model.creator.mode === Influencer.Mode.Live.value;

  const goHome = () => {
    const path = isCreatorView
      ? PATHS.HOME
      : PATHS.HOME_CREATOR(model.creator.id);
    browserHistory.push(path);
  };

  return {
    type: 'fetched',
    value: {
      tabs,
      publishAction: getPublishAction(model.publishing),
      publishModal: {
        isOpen: publishState.showPublishModal,
        onClose: () =>
          setPublishState((prev) => ({ ...prev, showPublishModal: false })),
        publish: () => {
          model.publishProgram();
          setPublishState((prev) => ({ ...prev, showPublishModal: false }));
        },
        showExamplesModal: publishState.showExamplesModal,
        closeExamplesModal: () =>
          setPublishState((prev) => ({ ...prev, showExamplesModal: false })),
        openExamplesModal: () =>
          setPublishState((prev) => ({ ...prev, showExamplesModal: true })),
        alert,
        firstTimePublishInfo: getPublishInfo(
          hasPublishedAnything,
          model.collection.accessType,
        ),
        hideSaveAsDraft: isArchived,
        isCreatorLive,
      },
      publishToBundleModal: {
        isOpen: publishState.showPublishToBundleModal,
        close: () =>
          setPublishState((prev) => ({
            ...prev,
            showPublishToBundleModal: false,
          })),
        programId: model.collection.id,
        publish: () => {
          model.publishProgram();
          setPublishState((prev) => ({
            ...prev,
            showPublishToBundleModal: false,
          }));
        },
        setPublishToPlatformBundle: (value: boolean) => {
          model.setPublishToPlatformBundle(value);
        },
        isPlatformBundleSelected: model.publishToPlatformBundle,
      },
      removeFromBundleModal: {
        isOpen: publishState.showRemoveFromBundleModal,
        close: () =>
          setPublishState((prev) => ({
            ...prev,
            showRemoveFromBundleModal: false,
          })),
        programId: model.collection.id,
        archive: () => {
          model.archiveProgram();
          setPublishState((prev) => ({
            ...prev,
            showRemoveFromBundleModal: false,
          }));
        },
      },
      archiveModal: {
        isOpen: publishState.showArchiveModal,
        close: () =>
          setPublishState((prev) => ({ ...prev, showArchiveModal: false })),
        archive: () => {
          model.archiveProgram();
          setPublishState((prev) => ({ ...prev, showArchiveModal: false }));
        },
      },
      programCard: {
        title: liteProgram.title || '',
        subtitle: liteProgram.subtitle || '',
        thumbnailUrl: liteProgram.thumbnailUrl,
        price: formatPrice(model.price?.price || 0),
        discountedPrice: formatPrice(model.price?.discountedPrice || null),
      },
      successModal: {
        isOpen: model.openModal === 'success',
        onClose: () => model.closeModal(),
        ...getSuccessModalData({
          accessType: model.collection.accessType,
          liteProgram,
          customURL,
          profileDeepLink,
          programDeepLink: model.programDeepLink,
          creatorId,
          isCreatorLive,
          hasExclusivePricing:
            !!model.collection.exclusiveProgramMetadata?.price ||
            !!model.collection.exclusiveProgramMetadata?.originalPrice,
          dismiss: () => model.closeModal(),
          goHome,
        }),
      },
      tabProps: {
        disabledCollectionType: liteProgram.programType === 'otp',
        collection: getMappedCollection(model.collection as any),
        originalAccessType: model.originalValues?.accessType || 'N/A',
        liteProgram,
        validationTree: model.validationTree,
        onChange: (p) => model.onChange(p),
        typeChanged: (t) => model.onTypeChange(t),
        params: {
          influencerId: creatorId.toString(),
          programId: collectionId.toString(),
        },
        save: model.save,
        refetchCollection: model.refetch,
        router,
        route,
      },
      validationErrors: model.validationErrors,
      readonly,
    },
  };
};
